import React, { useState, useRef } from 'react';
import { Upload, Loader2, Download, FileAudio, Film, CheckCircle2, RotateCcw } from 'lucide-react';
import { uploadForm, downloadFile, fmtSize, fmtDur, ACCEPT_MEDIA } from './utileApi';

// Vidéo (ou audio) -> MP3 : extrait uniquement le son. La vidéo d'origine n'est jamais conservée sur le serveur.
export default function Mp3Tool({ status, reload }) {
  const [file, setFile] = useState(null);
  const [bitrate, setBitrate] = useState(192);
  const [pct, setPct] = useState(null);       // % d'envoi ; 100 = envoi fini, conversion en cours
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pick = (f) => {
    setError('');
    if (!f) return;
    if (f.size > 400 * 1048576) { setError('Fichier trop volumineux (maximum 400 Mo).'); return; }
    setFile(f);
  };

  const start = async () => {
    if (!file) return;
    setError(''); setBusy(true); setPct(0);
    const form = new FormData();
    form.append('file', file); form.append('bitrate', String(bitrate));
    const { status: st, data } = await uploadForm('/api/utile/mp3', form, setPct);
    setBusy(false); setPct(null);
    if (st === 200 && data.success) { setJob(data.job); setFile(null); reload(); }
    else setError(data.message || 'Conversion impossible. Réessayez.');
  };

  const download = async () => {
    try { await downloadFile(`/api/utile/jobs/${job.id}/file`, job.file_name); }
    catch (e) { setError(e.message); }
  };

  if (job) {
    return (
      <div className="space-y-4">
        <div className="rounded-3xl bg-white/5 border border-emerald-500/30 p-6 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-2" />
          <p className="font-semibold break-all">{job.file_name}</p>
          <p className="text-xs text-blue-200 mt-1">{job.size_mb} Mo · {fmtDur(job.duration_s)}</p>
          <button onClick={download} className="mt-5 w-full py-3 rounded-2xl font-bold bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 transition flex items-center justify-center gap-2">
            <Download className="w-5 h-5" /> Télécharger le MP3
          </button>
        </div>
        {error && <p className="text-rose-300 text-sm">{error}</p>}
        <button onClick={() => { setJob(null); setError(''); }} className="w-full py-3 rounded-2xl bg-white/10 hover:bg-white/20 transition font-semibold flex items-center justify-center gap-2">
          <RotateCcw className="w-4 h-4" /> Convertir un autre fichier
        </button>
        <p className="text-xs text-gray-400">Le fichier est supprimé du serveur après 6 heures. Votre vidéo d'origine n'est jamais conservée.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        onClick={() => !busy && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
        className={`cursor-pointer rounded-3xl border-2 border-dashed p-8 text-center transition ${drag ? 'border-emerald-400 bg-emerald-500/10' : 'border-white/20 bg-white/5 hover:bg-white/10'}`}
      >
        <input ref={inputRef} type="file" accept={ACCEPT_MEDIA} className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
        {file ? (
          <div className="flex flex-col items-center gap-2">
            {file.type.startsWith('video') ? <Film className="w-10 h-10 text-emerald-300" /> : <FileAudio className="w-10 h-10 text-emerald-300" />}
            <p className="font-semibold break-all">{file.name}</p>
            <p className="text-sm text-blue-200">{fmtSize(file.size)} — cliquez pour changer</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-10 h-10 text-emerald-300" />
            <p className="font-semibold">Glissez votre vidéo ici</p>
            <p className="text-sm text-blue-200">ou cliquez pour choisir un fichier</p>
            <p className="text-xs text-gray-400 mt-2">mp4, mov, mkv, avi, webm… (ou un audio à convertir en MP3)<br />Maximum 400 Mo et 60 minutes</p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 text-sm">
        <span className="text-blue-200">Qualité du MP3 :</span>
        {[128, 192, 320].map(b => (
          <button key={b} onClick={() => setBitrate(b)}
            className={`px-3 py-1.5 rounded-xl border transition ${bitrate === b ? 'bg-emerald-500/25 border-emerald-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>{b} kbps</button>
        ))}
      </div>

      {pct !== null && (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <p className="text-sm mb-2 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {pct < 100 ? `Envoi du fichier… ${pct}%` : 'Extraction du son…'}</p>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
      )}
      {error && <p className="text-rose-300 text-sm bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3">{error}</p>}

      <button onClick={start} disabled={!file || busy}
        className="w-full py-3.5 rounded-2xl font-bold bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 disabled:opacity-40 transition flex items-center justify-center gap-2">
        <FileAudio className="w-5 h-5" /> Extraire le MP3
      </button>
      {status && <p className="text-xs text-gray-400 text-center">Gratuit · {status.mp3_left} / {status.mp3_limit} conversions restantes aujourd'hui</p>}
    </div>
  );
}
